import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Target, 
  Activity, 
  History, 
  Award, 
  Zap, 
  Leaf, 
  RefreshCw, 
  Crown,          
  FileText,       
  Wallet,         
  ClipboardCheck, 
  UserCog,        
  Megaphone,      
  Coins,          
  Camera,         
  LayoutGrid,
  GraduationCap
} from 'lucide-react';
import { usePeriod } from './PeriodContext';
import { getPeriodData, defaultDivisions } from './dataService';

// Icon mapping
const iconMap = {
  Crown: <Crown className="w-6 h-6 text-yellow-400" />,
  FileText: <FileText className="w-6 h-6 text-blue-400" />,
  Wallet: <Wallet className="w-6 h-6 text-emerald-400" />,
  ClipboardCheck: <ClipboardCheck className="w-6 h-6 text-indigo-400" />,
  UserCog: <UserCog className="w-6 h-6 text-pink-400" />,
  Megaphone: <Megaphone className="w-6 h-6 text-orange-400" />,
  Coins: <Coins className="w-6 h-6 text-teal-400" />,
  Camera: <Camera className="w-6 h-6 text-purple-400" />
};

const colorMap = {
  yellow: "border-yellow-500/20 bg-yellow-500/5 hover:border-yellow-500/40",
  blue: "border-blue-500/20 bg-blue-500/5 hover:border-blue-500/40",
  emerald: "border-emerald-500/20 bg-emerald-500/5 hover:border-emerald-500/40",
  indigo: "border-indigo-500/20 bg-indigo-500/5 hover:border-indigo-500/40",
  pink: "border-pink-500/20 bg-pink-500/5 hover:border-pink-500/40",
  orange: "border-orange-500/20 bg-orange-500/5 hover:border-orange-500/40",
  teal: "border-teal-500/20 bg-teal-500/5 hover:border-teal-500/40",
  purple: "border-purple-500/20 bg-purple-500/5 hover:border-purple-500/40"
};

// Icon quote sederhana SVG inline
function QuoteIcon({ className }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M14.017 21L14.017 18C14.017 16.8954 14.9124 16 16.017 16H19.017C19.5693 16 20.017 15.5523 20.017 15V9C20.017 8.44772 19.5693 8 19.017 8H15.017C14.4647 8 14.017 8.44772 14.017 9V11C14.017 11.5523 13.5693 12 13.017 12H12.017V5H22.017V15C22.017 18.3137 19.3307 21 16.017 21H14.017ZM5.01691 21L5.01691 18C5.01691 16.8954 5.91234 16 7.01691 16H10.0169C10.5692 16 11.0169 15.5523 11.0169 15V9C11.0169 8.44772 10.5692 8 10.0169 8H6.01691C5.46462 8 5.01691 8.44772 5.01691 9V11C5.01691 11.5523 4.56919 12 4.01691 12H3.01691V5H13.0169V15C13.0169 18.3137 10.3306 21 7.01691 21H5.01691Z" />
    </svg>
  )
}

// Card Component
function Card({ icon, title, desc, color }) {
  return (
    <div className={`p-8 rounded-2xl border ${color} bg-slate-900/50 hover:bg-slate-900 transition-all duration-300 group hover:-translate-y-1 h-full`}>
      <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center mb-6 border border-slate-700 group-hover:border-slate-600 transition-colors">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-indigo-400 transition-colors">
        {title}
      </h3>
      <p className="text-slate-400 text-sm leading-relaxed">
        {desc}
      </p>
    </div>
  );
}

function About() {
  const { selectedPeriod, organizationData, periods, setSelectedPeriod, periodImages } = usePeriod();
  const [periodPersonel, setPeriodPersonel] = useState(null);
  const [scrollY, setScrollY] = useState(0);

  // Parallax Scroll Listener
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // State for drag functionality
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(75);

  // Background image: use period-specific first, then fallback to default
  const backgroundImage = periodImages?.backgroundImage 
    ? periodImages.backgroundImage 
    : (selectedPeriod === '2025-2026' ? '/bg.jpg' : '/bg.jpg');

  // Default organization data
  const defaultOrgData = {
    nama: "FKPO Kulon Progo",
    visi: "Menjadikan FKPO Kulon Progo sebagai pionir dalam mewujudkan perubahan sosial yang progresif dan berkelanjutan melalui inovasi, kolaborasi, dan kepemimpinan yang inspiratif dan independen.",
    misi: [
      {
        title: "Meningkatkan Partisipasi",
        desc: "Mengajak seluruh anggota untuk aktif berpartisipasi dalam setiap kegiatan dan program organisasi guna memperkukuh solidaritas, menguatkan nilai-nilai kebersamaan dan mengembangkan rasa kepedulian siswa terhadap lingkungan sekitarnya."
      },
      {
        title: "Pemberdayaan SDM",
        desc: "Memberdayakan siswa supaya menjadi agen perubahan dengan memberikan pelatihan kepemimpinan, keterampilan, dan kesempatan berkontribusi dalam skema sosial yang nyata."
      },
      {
        title: "Sustainabilitas & Dampak Positif",
        desc: "Menerapkan kaidah keberlanjutan dalam setiap aktivitas organisasi dan memastikan bahwa setiap program memberikan dampak positif yang nyata dan berjangka panjang bagi siswa."
      },
      {
        title: "Transformasi Program Kerja",
        desc: "Melakukan transformasi dan keberlanjutan Program Kerja periode sebelumnya dengan inovasi baru untuk menjaga kontinuitas hal baik yang telah dibangun."
      }
    ],
    sejarah: "FKPO Kulon Progo berdiri pada tahun 2017 sebagai wadah komunikasi dan koordinasi antar pelajar dari berbagai organisasi sekolah di wilayah Kulon Progo. Forum ini lahir dari keprihatinan terhadap permasalahan remaja seperti pergaulan bebas dan rendahnya semangat belajar.",
    tentangLainnya: "FKPO (Forum Komunikasi Pengurus OSIS) Kulon Progo merupakan wadah aktif yang rutin memperbarui kepengurusannya setiap tahun di bawah naungan Balai Pemuda dan Olahraga (BPO) Dinas Pendidikan Pemuda dan Olahraga DIY."
  };

  const orgData = organizationData || defaultOrgData;
  
  // Get divisions - use custom or default
  const divisions = orgData?.divisions || defaultDivisions;

  // Safe divisions array
  const safeDivisions = Array.isArray(divisions) ? divisions : [];

  // Sync drag position with selected period
  useEffect(() => {
    const periodIndex = periods.findIndex(p => p.tahun === selectedPeriod);
    if (periodIndex === 0) {
      setDragPosition(75);
    } else if (periodIndex > 0) {
      setDragPosition(25);
    }
  }, [selectedPeriod, periods]);

  // Fetch period-specific personel data
  useEffect(() => {
    const fetchPeriodData = async () => {
      const data = await getPeriodData(selectedPeriod);
      setPeriodPersonel(data);
    };
    fetchPeriodData();
  }, [selectedPeriod]);

  // Drag handler functions
  const updatePosition = (clientX) => {
    const container = document.getElementById('timeline-container');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));

    setDragPosition(percentage);

    // Update year based on position
    if (periods.length > 1) {
      const midPoint = 50;
      if (percentage < midPoint && selectedPeriod !== periods[1]?.tahun) {
        // Go to older period
      } else if (percentage >= midPoint && selectedPeriod !== periods[0]?.tahun) {
        // Go to newer period
      }
    } else if (periods.length === 1) {
      setDragPosition(75);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updatePosition(e.clientX);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      updatePosition(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    updatePosition(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      e.preventDefault();
      updatePosition(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Get personnel for current period
  const getPersonnelForPeriod = () => {
    if (periodPersonel && periodPersonel.divisions) {
      return periodPersonel.divisions.map(div => ({
        role: div.title,
        members: div.members || []
      }));
    }
    return [];
  };

  const personnel = getPersonnelForPeriod();

  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 overflow-hidden">
      {/* Background Image Layer (Fixed & Calm) */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25 filter blur-[1px] pointer-events-none"
        style={{ 
          backgroundImage: `url(${backgroundImage})`
        }}
      />

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />

      {/* Dynamic Ambient Glows */}
      <div 
        className="absolute top-12 left-[-80px] w-96 h-96 bg-indigo-600/20 rounded-full blur-[120px] animate-blob-1 pointer-events-none" 
        style={{ transform: `translateY(${scrollY * -0.15}px)` }}
      />
      <div 
        className="absolute top-1/2 right-[-100px] w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[140px] animate-blob-2 pointer-events-none" 
        style={{ transform: `translateY(${scrollY * 0.12}px)` }}
      />

      {/* Overlay & Content Container */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center">
        <main className="flex-grow pt-28 pb-16 text-slate-100 font-sans selection:bg-indigo-500 selection:text-white">
          <div className="container mx-auto px-4 sm:px-6">
            
            {/* --- HERO / JUDUL HALAMAN --- */}
            <div 
              className="text-center mb-20 transition-transform duration-200 ease-out"
              style={{ transform: `translateY(${scrollY * -0.05}px)` }}
            >
              <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-400 text-xs font-bold tracking-widest uppercase shadow-lg shadow-indigo-950/40">
                <Users className="w-3.5 h-3.5" />
                <span>Profil Organisasi</span>
              </div>
              <h1 className="text-4xl sm:text-6xl font-black text-white mb-6 tracking-tight">
                Mengenal <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-purple-300 to-pink-400 drop-shadow-[0_0_30px_rgba(168,85,247,0.3)]">{orgData.nama}</span>
              </h1>
              <p className="text-base sm:text-xl text-slate-300/90 max-w-3xl mx-auto leading-relaxed">
                {orgData.tentangLainnya || "Wadah aspirasi, kolaborasi, dan aksi nyata bagi para pemimpin pelajar se Kulon Progo untuk mewujudkan perubahan."}
              </p>
            </div>

            {/* --- SEJARAH --- */}
            <div className="max-w-5xl mx-auto mb-24">
              <div className="relative bg-slate-900 border border-slate-800 rounded-3xl p-8 md:p-12 overflow-hidden shadow-2xl">
                {/* Dekorasi Background */}
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-indigo-600/10 rounded-full blur-[80px]"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-purple-600/10 rounded-full blur-[80px]"></div>
                
                <div className="relative z-10">
                  <div className="flex flex-col md:flex-row gap-8 items-start">
                    
                    {/* Kolom Ikon & Judul */}
                    <div className="md:w-1/3 flex flex-col gap-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/20 text-white">
                        <History className="w-8 h-8" />
                      </div>
                      <div>
                        <h2 className="text-3xl font-bold text-white mb-2">Sejarah Perjalanan</h2>
                        <div className="h-1 w-20 bg-indigo-500 rounded-full"></div>
                      </div>
                      
                      {/* Highlight Box Kecil */}
                      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 backdrop-blur-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Award className="w-5 h-5 text-yellow-500" />
                          <span className="text-sm font-semibold text-slate-200">Naungan Resmi</span>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed">
                         Balai Dikmen Kulon Progo & FKPO DIY
                        </p>
                      </div>
                    </div>

                    {/* Kolom Teks Sejarah */}
                    <div className="md:w-2/3 text-slate-300 leading-relaxed text-lg space-y-6">
                      <p>{orgData.sejarah || defaultOrgData.sejarah}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* --- VISI SECTION (FEATURED) --- */}
            <div className="max-w-5xl mx-auto mb-20">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-white inline-flex items-center gap-3">
                  <Target className="w-8 h-8 text-indigo-500" />
                  Visi Organisasi
                </h2>
              </div>
              
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-1000"></div>
                <div className="relative bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-12 text-center shadow-2xl">
                  <QuoteIcon className="w-12 h-12 text-slate-700 mx-auto mb-6 opacity-50" />
                  <p className="text-xl md:text-2xl font-medium text-slate-100 leading-relaxed italic font-serif">
                    "{orgData.visi || defaultOrgData.visi}"
                  </p>
                </div>
              </div>
            </div>

            {/* --- MISI SECTION (GRID LAYOUT) --- */}
            <div className="max-w-5xl mx-auto mb-24">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white inline-flex items-center gap-3">
                  <Activity className="w-8 h-8 text-green-500" />
                  Misi Kami
                </h2>
                <p className="text-slate-400 mt-4">Langkah nyata yang kami tempuh untuk mewujudkan visi.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {(orgData.misi || defaultOrgData.misi).map((item, index) => (
                  <Card 
                    key={index}
                    icon={
                      index === 0 ? <Users className="w-6 h-6 text-blue-400" /> :
                      index === 1 ? <Zap className="w-6 h-6 text-yellow-400" /> :
                      index === 2 ? <Leaf className="w-6 h-6 text-green-400" /> :
                      <RefreshCw className="w-6 h-6 text-purple-400" />
                    }
                    color={
                      index === 0 ? "border-blue-500/20 bg-blue-500/5" :
                      index === 1 ? "border-yellow-500/20 bg-yellow-500/5" :
                      index === 2 ? "border-green-500/20 bg-green-500/5" :
                      "border-purple-500/20 bg-purple-500/5"
                    }
                    title={item.title}
                    desc={item.desc}
                  />
                ))}
              </div>
            </div>

            {/* --- DIVISI / DESKRIPSI SECTION --- */}
            <div className="max-w-6xl mx-auto mb-24">
              <div className="text-center mb-12">
                <div className="w-24 h-1 bg-gradient-to-r from-transparent via-slate-700 to-transparent mx-auto mb-10"></div>
                
                <h2 className="text-3xl font-bold text-white inline-flex items-center gap-3">
                  <LayoutGrid className="w-8 h-8 text-indigo-500" />
                  Divisi & Tugas
                </h2>
                <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
                  Organisasi kami digerakkan oleh tim yang berdedikasi dengan peran dan tanggung jawab spesifik untuk mencapai tujuan bersama.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {safeDivisions.map((item, index) => (
                  <Card 
                    key={index}
                    icon={iconMap[item.icon] || <LayoutGrid className="w-6 h-6 text-indigo-400" />}
                    title={item.title}
                    desc={item.desc}
                    color={colorMap[item.color] || "border-indigo-500/20 bg-indigo-500/5"}
                  />
                ))}
              </div>
            </div>

            {/* --- STRUKTUR KEPENGURUSAN (PERSONALIA) --- */}
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                {/* Timeline Toggle */}
                {periods.length > 0 && (
                  <div className="mb-8">
                    <div className="text-slate-300 text-lg font-semibold mb-6">Periode</div>
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-12 mb-4">
                        {periods.slice().reverse().map((period, idx) => (
                          <span
                            key={period.id}
                            className={`text-base font-semibold transition-colors cursor-pointer hover:text-indigo-300 ${selectedPeriod === period.tahun ? 'text-indigo-400' : 'text-slate-400'}`}
                            onClick={() => setSelectedPeriod(period.tahun)}
                          >
                            {period.tahun}
                          </span>
                        ))}
                      </div>
                      <div
                        id="timeline-container"
                        className="relative flex items-center cursor-grab active:cursor-grabbing select-none"
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{ width: '160px' }}
                      >
                        {/* Background line */}
                        <div className="w-32 h-1.5 bg-slate-600 mx-3 rounded-full"></div>

                        {/* Draggable indicator */}
                        <div
                          className="absolute w-6 h-6 bg-indigo-500 border-2 border-indigo-300 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                          style={{
                            left: `${dragPosition}%`,
                            transform: 'translateX(-50%)',
                            cursor: isDragging ? 'grabbing' : 'grab'
                          }}
                        ></div>

                        {/* Invisible overlay for better touch */}
                        <div className="absolute inset-0 w-full h-8 -m-1"></div>
                      </div>
                    </div>
                  </div>
                )}

                <h2 className="text-3xl font-bold text-white">
                  Struktur Kepengurusan {orgData.nama} {selectedPeriod}
                </h2>
                <p className="text-slate-400 mt-4">
                  Putra-putri terbaik dari berbagai sekolah se Kulon Progo.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {personnel.length > 0 ? (
                  personnel.map((group, index) => (
                    <div key={index} className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden hover:border-slate-700 transition-colors">
                      <div className="bg-slate-900/80 px-6 py-4 border-b border-slate-800">
                        <h3 className="text-lg font-bold text-indigo-400">
                          {group.role}
                        </h3>
                      </div>
                      <div className="p-6">
                        <ul className="space-y-4">
                          {group.members && group.members.map((member, mIndex) => (
                            <li key={mIndex} className="flex flex-col">
                              <span className="text-slate-100 font-medium text-base">
                                {member.name}
                              </span>
                              <div className="flex items-center gap-2 mt-1 text-slate-500 text-xs">
                                <GraduationCap className="w-3.5 h-3.5" />
                                <span>{member.school}</span>
                              </div>
                            </li>
                          ))}
                          {(!group.members || group.members.length === 0) && (
                            <li className="text-slate-500 text-sm italic">Belum ada data</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))
                ) : (
                  // Show message if no personnel data
                  <div className="col-span-full text-center py-12">
                    <p className="text-slate-500 text-lg">Belum ada data struktur kepengurus periode {selectedPeriod}</p>
                  </div>
                )}
              </div>
            </div>
            
          </div>
        </main>
      </div>
    </div>
  );
}

export default About;

